import React, { useEffect, useState, useMemo } from "react";
import { Loader2, Pencil, Save, X, Search, Plus, Trash2, GripVertical, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useSiteContent } from "../hooks/useSiteContent";

const CATEGORY_LABELS = {
  hero: "Anasayfa (Hero)",
  about: "Hakkımızda",
  iletisim: "İletişim",
  footer: "Footer",
};
const CATEGORY_ORDER = ["hero", "about", "iletisim", "footer"];

export default function ContentManager() {
  const { refresh: refreshPublic } = useSiteContent();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("hero");
  const [search, setSearch] = useState("");
  const [editingKey, setEditingKey] = useState(null);
  const [draftValue, setDraftValue] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/content");
      setItems(res.data.items || []);
    } catch (e) {
      toast.error("İçerik yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return items
      .filter((i) => i.category === activeCategory)
      .filter((i) => {
        if (!search.trim()) return true;
        const q = search.toLowerCase();
        return (
          i.label?.toLowerCase().includes(q) ||
          i.key?.toLowerCase().includes(q) ||
          (typeof i.value === "string" && i.value.toLowerCase().includes(q))
        );
      });
  }, [items, activeCategory, search]);

  const startEdit = (item) => {
    setEditingKey(item.key);
    // Clone deeply for JSON
    setDraftValue(item.type === "json" ? JSON.parse(JSON.stringify(item.value)) : item.value);
  };

  const cancelEdit = () => {
    setEditingKey(null);
    setDraftValue(null);
  };

  const saveEdit = async (item) => {
    setSaving(true);
    try {
      await api.put(`/admin/content/${encodeURIComponent(item.key)}`, { value: draftValue });
      toast.success("Kaydedildi — site güncellendi");
      setEditingKey(null);
      setDraftValue(null);
      await load();
      await refreshPublic();
    } catch (e) {
      toast.error("Kaydedilemedi");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="animate-spin text-white" /></div>;
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6" data-testid="content-panel">
      {/* Sidebar */}
      <aside className="md:col-span-3">
        <div className="overline mb-4">Kategoriler</div>
        <div className="flex md:flex-col gap-2">
          {CATEGORY_ORDER.map((cat) => {
            const count = items.filter((i) => i.category === cat).length;
            return (
              <button
                key={cat}
                onClick={() => { setActiveCategory(cat); setEditingKey(null); }}
                data-testid={`content-cat-${cat}`}
                className={`text-left px-4 py-3 border text-sm tracking-wide transition-colors ${
                  activeCategory === cat
                    ? "border-white text-white bg-[var(--bg)]"
                    : "border-[var(--border)] text-[var(--text-muted)] hover:border-white/40 hover:text-white"
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span>{CATEGORY_LABELS[cat]}</span>
                  <span className="text-xs opacity-60">{count}</span>
                </div>
              </button>
            );
          })}
        </div>

        <a
          href="/"
          target="_blank"
          rel="noreferrer"
          className="mt-6 hidden md:flex items-center gap-2 px-4 py-3 border border-[var(--border)] text-xs uppercase tracking-widest text-[var(--text-muted)] hover:text-white hover:border-white transition-colors"
          data-testid="content-preview-site"
        >
          <ExternalLink size={14} /> Siteyi Önizle
        </a>
      </aside>

      {/* Right Panel */}
      <section className="md:col-span-9">
        <div className="flex items-center gap-3 mb-6">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="İçerikte ara..."
              className="input-sharp pl-9"
              data-testid="content-search"
            />
          </div>
          <div className="text-xs uppercase tracking-widest text-[var(--text-muted)]">
            {filtered.length} öğe
          </div>
        </div>

        <div className="space-y-4">
          {filtered.map((item) => (
            <ContentCard
              key={item.key}
              item={item}
              isEditing={editingKey === item.key}
              draftValue={draftValue}
              setDraftValue={setDraftValue}
              onStartEdit={() => startEdit(item)}
              onCancel={cancelEdit}
              onSave={() => saveEdit(item)}
              saving={saving}
            />
          ))}
          {filtered.length === 0 && (
            <div className="border border-dashed border-[var(--border)] p-12 text-center text-[var(--text-muted)] text-sm">
              Bu kategoride öğe bulunamadı.
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function ContentCard({ item, isEditing, draftValue, setDraftValue, onStartEdit, onCancel, onSave, saving }) {
  return (
    <div className="border border-[var(--border)] bg-[var(--surface)] p-5" data-testid={`content-card-${item.key}`}>
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <div className="text-sm font-medium text-white">{item.label}</div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] mt-1 font-mono">{item.key}</div>
        </div>
        {!isEditing ? (
          <button
            onClick={onStartEdit}
            className="flex items-center gap-2 px-3 py-1.5 border border-[var(--border)] text-xs uppercase tracking-widest text-white hover:border-white"
            data-testid={`content-edit-${item.key}`}
          >
            <Pencil size={12} /> Düzenle
          </button>
        ) : (
          <div className="flex gap-2">
            <button
              onClick={onCancel}
              className="flex items-center gap-2 px-3 py-1.5 border border-[var(--border)] text-xs uppercase tracking-widest text-[var(--text-muted)] hover:text-white"
              data-testid={`content-cancel-${item.key}`}
            >
              <X size={12} /> İptal
            </button>
            <button
              onClick={onSave}
              disabled={saving}
              className="flex items-center gap-2 px-3 py-1.5 border border-white bg-white text-black text-xs uppercase tracking-widest hover:bg-transparent hover:text-white disabled:opacity-60"
              data-testid={`content-save-${item.key}`}
            >
              {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Kaydet
            </button>
          </div>
        )}
      </div>

      {/* Display or Edit area */}
      {!isEditing ? (
        <ContentDisplay item={item} />
      ) : (
        <ContentEditor item={item} value={draftValue} onChange={setDraftValue} />
      )}
    </div>
  );
}

function ContentDisplay({ item }) {
  if (item.type === "json" && item.key === "contact.offices") {
    return (
      <div className="space-y-2 text-sm text-[var(--text-muted)]">
        {(item.value || []).map((o, i) => (
          <div key={i} className="border-l-2 border-[var(--border)] pl-3 py-1">
            <div className="text-white">{o.title}</div>
            <div className="text-xs">{o.address}</div>
            {(o.phones || []).length > 0 && <div className="text-xs">📞 {o.phones.join(" / ")}</div>}
            {(o.emails || []).length > 0 && <div className="text-xs">✉️ {o.emails.join(" / ")}</div>}
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="text-sm text-[var(--text-muted)] whitespace-pre-wrap break-words">
      {typeof item.value === "string" ? item.value : JSON.stringify(item.value, null, 2)}
    </div>
  );
}

function ContentEditor({ item, value, onChange }) {
  if (item.type === "json" && item.key === "contact.offices") {
    return <OfficesEditor value={value || []} onChange={onChange} />;
  }
  if (item.type === "textarea") {
    return (
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        rows={4}
        className="input-sharp resize-none w-full"
        data-testid={`content-input-${item.key}`}
      />
    );
  }
  return (
    <input
      type="text"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="input-sharp w-full"
      data-testid={`content-input-${item.key}`}
    />
  );
}

function OfficesEditor({ value, onChange }) {
  const addOffice = () => {
    onChange([
      ...value,
      { key: `office-${Date.now()}`, title: "Yeni Ofis", address: "", phones: [], emails: [] },
    ]);
  };
  const updateOfficeAt = (idx, next) => {
    onChange(value.map((o, i) => (i === idx ? next : o)));
  };
  const removeOffice = (idx) => {
    if (!window.confirm("Bu ofis silinsin mi?")) return;
    onChange(value.filter((_, i) => i !== idx));
  };
  const moveOffice = (idx, dir) => {
    const next = [...value];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  return (
    <div className="space-y-4">
      {value.map((office, idx) => (
        <OfficeRow
          key={office.key || idx}
          office={office}
          idx={idx}
          isLast={idx === value.length - 1}
          isFirst={idx === 0}
          onUpdate={(next) => updateOfficeAt(idx, next)}
          onRemove={() => removeOffice(idx)}
          onMoveUp={() => moveOffice(idx, -1)}
          onMoveDown={() => moveOffice(idx, +1)}
        />
      ))}
      <button
        type="button"
        onClick={addOffice}
        className="w-full flex items-center justify-center gap-2 border border-dashed border-[var(--border)] py-3 text-sm text-[var(--text-muted)] hover:text-white hover:border-white"
        data-testid="office-add"
      >
        <Plus size={14} /> Yeni Ofis Ekle
      </button>
    </div>
  );
}

function OfficeRow({ office, idx, isFirst, isLast, onUpdate, onRemove, onMoveUp, onMoveDown }) {
  // Local raw text state preserves newlines while typing
  // (parent stores parsed arrays — empty lines were being stripped per keystroke, blocking Enter)
  const [phonesText, setPhonesText] = useState((office.phones || []).join("\n"));
  const [emailsText, setEmailsText] = useState((office.emails || []).join("\n"));

  const handlePhonesChange = (text) => {
    setPhonesText(text);
    onUpdate({
      ...office,
      phones: text.split("\n").map((s) => s.trim()).filter(Boolean),
    });
  };
  const handleEmailsChange = (text) => {
    setEmailsText(text);
    onUpdate({
      ...office,
      emails: text.split("\n").map((s) => s.trim()).filter(Boolean),
    });
  };

  return (
    <div className="border border-[var(--border)] p-4 bg-[var(--bg)]" data-testid={`office-editor-${idx}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <input
          value={office.title || ""}
          onChange={(e) => onUpdate({ ...office, title: e.target.value })}
          placeholder="Ofis Adı"
          className="input-sharp font-medium text-white"
          data-testid={`office-title-${idx}`}
        />
        <div className="flex gap-1">
          <button type="button" disabled={isFirst} onClick={onMoveUp} className="px-2 py-1.5 border border-[var(--border)] text-[var(--text-muted)] hover:text-white text-xs disabled:opacity-30">↑</button>
          <button type="button" disabled={isLast} onClick={onMoveDown} className="px-2 py-1.5 border border-[var(--border)] text-[var(--text-muted)] hover:text-white text-xs disabled:opacity-30">↓</button>
          <button type="button" onClick={onRemove} className="px-2 py-1.5 border border-red-900/50 text-red-400 hover:bg-red-950/30 text-xs" data-testid={`office-remove-${idx}`}><Trash2 size={12} /></button>
        </div>
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] mb-1 block">Adres</label>
          <input
            value={office.address || ""}
            onChange={(e) => onUpdate({ ...office, address: e.target.value })}
            placeholder="Adres"
            className="input-sharp"
            data-testid={`office-address-${idx}`}
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] mb-1 block">Telefonlar (her satıra bir tane — Enter ile alt satır)</label>
          <textarea
            value={phonesText}
            onChange={(e) => handlePhonesChange(e.target.value)}
            rows={3}
            placeholder={"0212 294 99 37\n0533 252 67 57\n0532 630 38 39"}
            className="input-sharp resize-y font-mono text-sm w-full whitespace-pre"
            data-testid={`office-phones-${idx}`}
          />
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] mb-1 block">E-postalar (her satıra bir tane — Enter ile alt satır)</label>
          <textarea
            value={emailsText}
            onChange={(e) => handleEmailsChange(e.target.value)}
            rows={3}
            placeholder={"info@demiray.net.tr\nsedat@demiray.net.tr"}
            className="input-sharp resize-y font-mono text-sm w-full whitespace-pre"
            data-testid={`office-emails-${idx}`}
          />
        </div>
      </div>
    </div>
  );
}
